import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Receipt as TransactionsIcon,
  Payments as DisbursementsIcon,
  Settings as SettingsIcon,
  NotificationsNone,
  Person,
  Logout,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import iconLogo from '../../assets/images/logos/icononly_transparent_nobuffer.png';
import textLogo from '../../assets/images/logos/textonly_nobuffer_transparent.png';

const GlobalNavigation: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { logout } = useAuth();

  // Map routes to tab indices
  const routeToTabIndex: { [key: string]: number } = {
    '/dashboard': 0,
    '/transactions': 1,
    '/disbursements': 2,
    '/settings': 3,
  };

  const currentTab = routeToTabIndex[location.pathname] || 0;

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    const routes = ['/dashboard', '/transactions', '/disbursements', '/settings'];
    navigate(routes[newValue]);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
  };

  return (
    <AppBar 
      position="fixed" 
      sx={{ 
        backdropFilter: 'blur(20px)',
        backgroundColor: 'rgba(255, 255, 255, 0.9)',
        boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)',
      }}
    >
      <Toolbar sx={{ justifyContent: 'space-between' }}>
        <Box sx={{ width: 220, display: 'flex', alignItems: 'center', gap: 1 }}>
          <img 
            src={iconLogo} 
            alt="Dizpurse Icon" 
            style={{ 
              height: '42px',
              width: '42px',
              filter: 'drop-shadow(0px 4px 8px rgba(0, 0, 0, 0.1))',
            }} 
          />
          <img
            src={textLogo}
            alt="Dizpurse Text Logo"
            style={{ height: '32px', marginTop: 10 }}
          />
        </Box>
        
        <Tabs 
          value={currentTab} 
          onChange={handleTabChange}
          sx={{ 
            '& .MuiTab-root': {
              color: theme.palette.text.secondary,
              minWidth: 80,
              '&.Mui-selected': {
                color: theme.palette.primary.main,
              },
            },
          }}
        >
          <Tab icon={<DashboardIcon />} label="Dashboard" />
          <Tab icon={<TransactionsIcon />} label="Transactions" />
          <Tab icon={<DisbursementsIcon />} label="Disbursements" />
          <Tab icon={<SettingsIcon />} label="Settings" />
        </Tabs>

        <Box sx={{ width: 120, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <IconButton 
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': { 
                color: theme.palette.primary.main,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <NotificationsNone />
          </IconButton>
          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{ 
              color: theme.palette.text.secondary,
              '&:hover': { 
                color: theme.palette.primary.main,
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            <Person />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleProfileMenuClose}
            PaperProps={{
              sx: {
                mt: 1.5,
                backdropFilter: 'blur(20px)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                boxShadow: '0px 2px 24px rgba(0, 0, 0, 0.1)',
              }
            }}
          >
            <MenuItem onClick={handleLogout} sx={{ gap: 1 }}>
              <Logout fontSize="small" />
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default GlobalNavigation; 